import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  buildNetboxTenantUrl,
  useCustomerDetail,
  useCustomersList,
  useTriggerCustomerSync,
} from './useNexusCustomers';
import { safeExternalUrl } from '../../utils/safeUrl';

/**
 * Nexus Customer-Übersicht (Spec 11 von 12).
 *
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-customers.md
 * Endpoints: /v1/admin/customers, /v1/admin/customers/sync
 *
 * NetBox-Auto-Sync läuft Backend-seitig stündlich; UI exposed manuellen
 * Trigger und zeigt `netbox_synced_at` pro Customer.
 */
export function NexusCustomersPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const query = useCustomersList({ search });
  const sync = useTriggerCustomerSync();
  const customers = query.data?.customers ?? [];

  return (
    <div>
      <PageHeader
        title="Nexus · Customers"
        subtitle={`${customers.length} sichtbar · NetBox-Auto-Sync stündlich`}
        actions={
          <HasPermission perms={['integrations.write']}>
            {canWrite && (
              <Button
                color="primary"
                outline
                size="sm"
                onClick={() => sync.mutate({})}
                disabled={sync.isPending}
              >
                {sync.isPending ? 'Sync läuft…' : '↻ Sync now'}
              </Button>
            )}
          </HasPermission>
        }
      />

      <Card className="mb-3">
        <CardBody className="py-2">
          <FormGroup className="mb-0" style={{ maxWidth: 360 }}>
            <Label className="small text-muted mb-1">Suche</Label>
            <Input
              type="search"
              bsSize="sm"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Display-Name / Email / External-ID"
            />
          </FormGroup>
        </CardBody>
      </Card>

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && customers.length === 0}
        emptyMessage="Keine Customers für die aktuelle Suche."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th>Display Name</th>
            <th style={{ width: 160 }}>External-ID</th>
            <th style={{ width: 100 }}>Services</th>
            <th style={{ width: 100 }}>Domains</th>
            <th style={{ width: 180 }}>NetBox-Tenant</th>
            <th style={{ width: 160 }}>Letzter Sync</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((c) => (
            <tr
              key={c.id}
              role="button"
              onClick={() => setOpenId(c.id)}
              style={{ cursor: 'pointer' }}
            >
              <td>
                {c.display_name ?? <span className="text-muted">—</span>}
                {c.email && <div className="text-muted small">{c.email}</div>}
              </td>
              <td className="small">
                {c.external_id ? (
                  <code>{c.external_id}</code>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td>{c.services_count}</td>
              <td>{c.domains_count}</td>
              <td className="small">
                <NetboxLink slug={c.netbox_slug} />
              </td>
              <td>
                {c.netbox_synced_at ? (
                  <RelativeTime iso={c.netbox_synced_at} />
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      <CustomerDetailDrawer
        customerId={openId}
        isOpen={!!openId}
        onClose={() => setOpenId(null)}
        canWrite={canWrite}
      />
    </div>
  );
}

function NetboxLink({ slug }) {
  if (!slug) return <span className="text-muted">—</span>;
  const url = safeExternalUrl(buildNetboxTenantUrl(slug));
  if (!url) return <code>{slug}</code>;
  return (
    <a href={url} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()}>
      <code>{slug}</code> <span>↗</span>
    </a>
  );
}

function CustomerDetailDrawer({ customerId, isOpen, onClose, canWrite }) {
  const detail = useCustomerDetail(customerId);
  const sync = useTriggerCustomerSync();
  const c = detail.data?.customer;
  const services = detail.data?.services ?? [];
  const domains = detail.data?.domains ?? [];

  return (
    <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" style={{ width: 640 }}>
      <OffcanvasHeader toggle={onClose}>
        {c?.display_name ?? 'Customer-Detail'}
      </OffcanvasHeader>
      <OffcanvasBody>
        {detail.isLoading && <p>Lade…</p>}
        {detail.isError && (
          <Alert color="danger">
            Customer konnte nicht geladen werden: {detail.error?.message}
          </Alert>
        )}
        {c && (
          <>
            <dl className="row small mb-3">
              <dt className="col-4 text-muted">External-ID</dt>
              <dd className="col-8">
                {c.external_id ? <code>{c.external_id}</code> : '—'}
              </dd>
              <dt className="col-4 text-muted">Tenant</dt>
              <dd className="col-8">{c.tenant_id ?? '—'}</dd>
              {c.email && (
                <>
                  <dt className="col-4 text-muted">Email</dt>
                  <dd className="col-8">{c.email}</dd>
                </>
              )}
              <dt className="col-4 text-muted">NetBox</dt>
              <dd className="col-8">
                <NetboxLink slug={c.netbox_slug} />
              </dd>
              <dt className="col-4 text-muted">Letzter Sync</dt>
              <dd className="col-8">
                {c.netbox_synced_at ? <RelativeTime iso={c.netbox_synced_at} /> : '—'}
              </dd>
            </dl>

            <h6 className="text-uppercase small text-muted">Services ({services.length})</h6>
            {services.length === 0 ? (
              <p className="small text-muted">Keine aktiven Services.</p>
            ) : (
              <ul className="list-unstyled small mb-3">
                {services.map((s) => (
                  <li key={s.id} className="d-flex gap-2 align-items-center mb-1">
                    <Badge color="secondary" pill className="orbit-code-badge-sm">
                      {s.plugin_id ?? s.kind ?? '—'}
                    </Badge>
                    <code>{s.account_id}</code>
                    {(s.subscription_id || s.external_id) && (
                      <span className="text-muted">· {s.subscription_id ?? s.external_id}</span>
                    )}
                    <Badge
                      color={s.status === 'active' ? 'success' : 'warning'}
                      pill
                      className="ms-auto"
                    >
                      {s.status}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}

            <h6 className="text-uppercase small text-muted">Domains ({domains.length})</h6>
            {domains.length === 0 ? (
              <p className="small text-muted">Keine Domains.</p>
            ) : (
              <ul className="list-unstyled small mb-3">
                {domains.map((d) => (
                  <li key={d.id} className="d-flex gap-2 align-items-center mb-1">
                    <code>{d.domain}</code>
                    <Badge color="info" pill className="orbit-code-badge-sm">
                      {d.kind ?? d.type ?? '—'}
                    </Badge>
                    {d.source && (
                      <span className="text-muted">{d.source}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <HasPermission perms={['integrations.write']}>
              {canWrite && (
                <ButtonGroup className="mt-3 d-flex flex-wrap">
                  <Button
                    color="primary"
                    outline
                    onClick={() => sync.mutate({})}
                    disabled={sync.isPending}
                  >
                    {sync.isPending ? 'Sync läuft…' : '↻ Sync now (alle Plugins)'}
                  </Button>
                  {safeExternalUrl(buildNetboxTenantUrl(c.netbox_slug)) && (
                    <Button
                      color="secondary"
                      outline
                      tag="a"
                      href={safeExternalUrl(buildNetboxTenantUrl(c.netbox_slug))}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Im NetBox öffnen ↗
                    </Button>
                  )}
                </ButtonGroup>
              )}
            </HasPermission>
          </>
        )}
      </OffcanvasBody>
    </Offcanvas>
  );
}
